import React, { useState, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { FaCheck } from "react-icons/fa";
import { FormField } from "../../UI/DesignStandards";
import { LoadingSpinner } from "../../UI/DesignStandards";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";

const PrimaryEventSelector = ({
  tournamentId,
  value = [],
  onChange,
  error,
  excludeEventId = null, // Event ID to exclude (for editing)
}) => {
  const [primaryEvents, setPrimaryEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchPrimaryEvents = async () => {
      if (!tournamentId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");

        // Use the database function to get available primary events
        const { data, error: fetchError } = await supabase.rpc(
          "get_available_primary_events_for_tournament",
          { tournament_id: tournamentId }
        );

        if (fetchError) throw fetchError;

        // Filter out the current event if editing
        const filteredEvents = excludeEventId
          ? (data || []).filter((event) => event.event_id !== excludeEventId)
          : data || [];

        setPrimaryEvents(filteredEvents);
      } catch (err) {
        console.error("Error fetching primary events:", err);
        setErrorMessage("Failed to load primary events");
      } finally {
        setLoading(false);
      }
    };

    fetchPrimaryEvents();
  }, [tournamentId, excludeEventId]);

  const handleToggle = (eventId) => {
    const currentValue = value || [];
    if (currentValue.includes(eventId)) {
      onChange(currentValue.filter((id) => id !== eventId));
    } else {
      onChange([...currentValue, eventId]);
    }
  };

  if (loading) {
    return (
      <div>
        <label className="block text-sm font-medium text-brand-charcoal mb-2">
          Primary Events <span className="text-red-500">*</span>
        </label>
        <div className="px-4 py-3 border border-brand-charcoal/20 rounded-lg bg-brand-cream/80 flex items-center justify-center">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-brand-muted">
            Loading primary events...
          </span>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div>
        <label className="block text-sm font-medium text-brand-charcoal mb-2">
          Primary Events <span className="text-red-500">*</span>
        </label>
        <InlineAlert type="error" message={errorMessage} />
      </div>
    );
  }

  if (primaryEvents.length === 0) {
    return (
      <div>
        <label className="block text-sm font-medium text-brand-charcoal mb-2">
          Primary Events <span className="text-red-500">*</span>
        </label>
        <InlineAlert
          type="warning"
          message="No primary events available. Create primary events first."
        />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-brand-charcoal mb-2">
        Primary Events <span className="text-red-500">*</span>{" "}
        <span className="text-brand-muted font-normal">(Select at least one)</span>
      </label>
      <div
        className={`border rounded-lg p-3 max-h-48 overflow-y-auto ${
          error ? "border-red-300" : "border-brand-charcoal/20"
        }`}
      >
        {primaryEvents.map((event) => {
          const isSelected = (value || []).includes(event.event_id);
          return (
            <label
              key={event.event_id}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-brand-cream/80 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(event.event_id)}
                className="w-4 h-4 text-brand-charcoal border-brand-charcoal/20 rounded focus:ring-brand-gold-ink"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-brand-charcoal">
                  {event.event_name}
                </span>
                {event.event_abbreviation && (
                  <span className="text-sm text-brand-muted ml-2">
                    ({event.event_abbreviation})
                  </span>
                )}
              </div>
              {isSelected && <FaCheck className="w-4 h-4 text-brand-charcoal" />}
            </label>
          );
        })}
      </div>
      {value && value.length > 0 && (
        <p className="mt-2 text-sm text-brand-muted">
          {value.length} primary event{value.length !== 1 ? "s" : ""} selected
        </p>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default PrimaryEventSelector;
